import { useState, useEffect } from "react";
import { Tooltip, Spin } from "antd";
import { Flex } from "@chakra-ui/layout";
import { MdAssignment, MdCheckCircle, MdClose } from "react-icons/md";
import { useFilters } from "core/features/filters/store";
import OperacoesTable from "./OperacoesTable";
import useStore from "../hooks/useStore";
import useModalOrdemServicoMatriz from "../hooks/useModalOrdemServicoMatriz";
import {
  StyledModal,
  Title,
  StyledTag,
  ButtonStyled,
  ButtonStyledLink,
  ContentButton,
  StyledCheckbox,
  SelectCompact,
  FooterContainer,
  HeaderContainer,
  ActionsContainer,
  HeaderContent,
  HeaderItem,
  HeaderDetails,
} from "./styles";

interface ModalMatrizOperacoesProps {
  data: any;
  isLoading: boolean;
  talhoesIds: any[];
  planoOperacionalData: any;
  isOpen: boolean;
  onClose: () => void;
  next: () => void;
  setOperationName: (args: string) => void;
  filteredResult: any[];
  orderedResult: any[];
}

function ModalMatrizOperacoes({
  data,
  isLoading,
  talhoesIds,
  planoOperacionalData,
  isOpen,
  onClose,
  next,
  setOperationName,
  filteredResult,
  orderedResult,
}: ModalMatrizOperacoesProps) {

  const [viewCompact, setViewCompact] = useState(false);
  const [available, setAvailable] = useState(0);

  const {
    isMatrizSubmitted,
    classesOperacoes,
    planoOperacionalOperacaoTalhaoIds,
    setClassesOperacoes,
    setPlanoOperacionalOperacaoTalhaoIds,
  } = useStore();

  const classes = Array.from(new Set(classesOperacoes));

  const {
    setHiddenBlockeds,
    setHiddenCompleteds,
  } = useModalOrdemServicoMatriz();

  const { filters } = useFilters();
  const { filtersAdvanced } = filters;

  const operacoesSelectedIds = filtersAdvanced.operacao;
  const operacoesFilters = planoOperacionalData.filtrosAvancados.operacoes;
  const operacoesSelected = operacoesFilters?.filter((o: any) => operacoesSelectedIds?.includes(o.operacaoId));
  const areaTotal = data?.reduce((acc: number, item: any) => acc += item.areaPlantadaHa, 0);

  const operacoes = data?.flatMap((operacao: any) => operacao?.planoOperacionalOperacaoTalhao?.map((operacaoTalhao: any) => operacaoTalhao.operacaoClasse));
  const operacoesSet = Array.from(new Set(operacoes));

  function handleSelectAllAvailable(checked: boolean) {
    const operacoes = filteredResult?.flatMap((t: any) => t.planoOperacionalOperacaoTalhao);
    const operacoesFiltered = operacoes?.filter(
      (o: any) =>
        !o.bloqueado &&
        !o.isPlaceholder &&
        o.areaDisponivelPorcentagem > 0 &&
        o.planoOperacionalOperacaoTalhaoId
    );
    const ids = operacoesFiltered?.map((o: any) => o.planoOperacionalOperacaoTalhaoId);
    const classesTalhoes = Array.from(new Set(operacoesFiltered.map((t: any) => t?.operacaoClasse)));
    setAvailable(ids.length);
    if (checked) {
      setClassesOperacoes(classesTalhoes as string[]);
      setPlanoOperacionalOperacaoTalhaoIds(ids);
      return;
    }
    setClassesOperacoes([]);
    setPlanoOperacionalOperacaoTalhaoIds([]);
  }

  function handleHiddenCompact() {
    setViewCompact(!viewCompact);
    setOperationName("");
  }

  useEffect(() => {
    if (isOpen && filteredResult && !isMatrizSubmitted) {
      handleSelectAllAvailable(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, filteredResult]);

  return (
    <StyledModal
      centered
      width={"95%"}
      zIndex={999999}
      visible={isOpen}
      onCancel={onClose}
      destroyOnClose
      title={
        <Title>
          <MdAssignment color="#fff" size={28} />
          Criação de Ordem de Serviço - Matriz de Operações
        </Title>
      }
      footer={
        <FooterContainer>
          <p><b>{planoOperacionalOperacaoTalhaoIds.length ?? 0}</b> operações selecionadas</p>
          <ContentButton>
            <ButtonStyledLink
              size="large"
              onClick={onClose}
              loading={isLoading}
              icon={<MdClose size={24} />}
            >Cancelar</ButtonStyledLink>
            <ButtonStyled
              type="primary"
              size="large"
              onClick={next}
              loading={isLoading}
              disabled={planoOperacionalOperacaoTalhaoIds.length === 0}
              icon={<MdCheckCircle size={24} />}
            >Próximo</ButtonStyled>
          </ContentButton>
        </FooterContainer>
      }
    >
      <HeaderContainer>
        <HeaderContent>
          <div>
            <p style={{ color: "#666666", fontWeight: 600 }}>Talhões Selecionados</p>
            <HeaderItem>
              {isLoading ? (
                <StyledTag color="blue">Carregando...</StyledTag>
              ) : (
                <>
                  {data?.slice(0, 4)?.map((item: any) => (
                    <StyledTag color="blue">{item.codigo}</StyledTag>
                  ))}
                  {data?.length > 4 && (
                    <Tooltip title={data?.slice(4)?.map((item: any) => item.codigo)?.join(", ")}>
                      <StyledTag color="blue">+{data?.slice(4)?.length}</StyledTag>
                    </Tooltip>
                  )}
                </>
              )}
            </HeaderItem>
          </div>
          {classes?.length > 0 && (
            <div>
              <p style={{ color: "#666666", fontWeight: 600 }}>Classes de Operações Selecionadas</p>
              <HeaderItem>
                {classes?.slice(0, 4)?.map((item: any) => (
                  <StyledTag color="green">{item}</StyledTag>
                ))}
                {classes?.length > 4 && (
                  <Tooltip title={classes?.slice(4)?.map((item: any) => item)?.join(", ")}>
                    <StyledTag color="green">+{classes?.slice(4)?.length}</StyledTag>
                  </Tooltip>
                )}
              </HeaderItem>
            </div>
          )}
          {operacoesSelected?.length > 0 && (
            <div>
              <p style={{ color: "#666666", fontWeight: 600 }}>Operações Selecionadas</p>
              <HeaderItem>
                {operacoesSelected?.map((item: any) => (
                  <StyledTag color="green">{item.descricao}</StyledTag>
                ))}
              </HeaderItem>
            </div>
          )}
        </HeaderContent>

        <HeaderDetails>
          <div>
            <b style={{ color: "#2196f3" }}>{talhoesIds.length ?? 0}</b>
            <p>TALHÕES</p>
          </div>
          <div>
            <b style={{ color: "#2196f3" }}>{areaTotal ? areaTotal.toFixed(2) : 0}</b>
            <p>ÁREA (HA)</p>
          </div>
          <div>
            <b style={{ color: "#2196f3" }}>{planoOperacionalOperacaoTalhaoIds.length ?? 0}</b>
            <p>SELECIONADAS</p>
          </div>
        </HeaderDetails>
      </HeaderContainer>

      <ActionsContainer>
        <StyledCheckbox
          checked={available === planoOperacionalOperacaoTalhaoIds.length}
          onChange={e => handleSelectAllAvailable(e.target.checked)}
          disabled={isLoading}
        >Marcar Todas Disponíveis</StyledCheckbox>
        <StyledCheckbox
          onChange={(e) => setHiddenCompleteds(e.target.checked)}
          disabled={isLoading}
        >Ocultar Concluídas</StyledCheckbox>
        <StyledCheckbox
          onChange={(e) => setHiddenBlockeds(e.target.checked)}
          disabled={isLoading}
        >Ocultar Bloqueadas</StyledCheckbox>
        <StyledCheckbox onChange={handleHiddenCompact} disabled={isLoading}>
          Visão por Classe
        </StyledCheckbox>
        {viewCompact && (
          <SelectCompact>
            <select onChange={(e) => setOperationName(e.target.value)} disabled={isLoading}>
              <option value="">Selecione a classe</option>
              {operacoesSet.map((item: any, index: number) => (
                <option key={index} value={item}>{item}</option>
              ))}
            </select>
          </SelectCompact>
        )}
      </ActionsContainer>

      {isLoading ? (
        <Flex
          justifyContent="center"
          alignItems="center"
          direction="column"
          minHeight="240px"
          gridGap={12}
        >
          <Spin size="large" />
          <p style={{ color: "#666666", margin: 0 }}>Carregando talhões...</p>
        </Flex>
      ) : (
        <OperacoesTable operacoes={orderedResult ?? []} />
      )}
    </StyledModal>
  );
}

export default ModalMatrizOperacoes;
